import { Box, Button, type ButtonProps, Modal, Stack } from "@mantine/core"
import { useDisclosure, useInterval, useScrollIntoView } from "@mantine/hooks"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import type { TUserSession } from "@/types"
import { SurveyStages } from "./stages"
import type { IStageProps } from "./stages/types"

export function Survey({ usersession, onClose, onStep }: Omit<IStageProps, "onNext">) {
  return (
    <Stack mih={400} miw={350} maw={900} h="100%" gap="xl">
      <SurveyStages onClose={onClose} usersession={usersession} onStep={onStep} />
    </Stack>
  )
}

export function SurveyButton({
  usersession,
  hideTrigger,
  ...props
}: { usersession: TUserSession; hideTrigger?: boolean } & ButtonProps) {
  const { t } = useTranslation()
  const [opened, { open, close }] = useDisclosure(false)
  const [dtnow, setDtnow] = useState(Date.now())
  const interval = useInterval(() => setDtnow(Date.now()), 10e3)

  useEffect(() => {
    if (usersession.session.dtstart > Date.now()) interval.start()
    else interval.stop()
    return () => interval.stop()
  }, [usersession.session.dtstart, interval])

  const { scrollIntoView, targetRef, scrollableRef } = useScrollIntoView<HTMLDivElement, HTMLDivElement>({
    duration: 300,
  })

  return (
    <>
      {!hideTrigger && (
        <Button
          {...props}
          onClick={open}
          variant="navy"
          size="compact-md"
          disabled={usersession.session.dtstart > dtnow}
        >
          {props.children || t("Take survey")}
        </Button>
      )}
      <Modal.Root opened={opened} onClose={close} size="xl" py={0} ref={scrollableRef}>
        <Modal.Overlay />
        <Modal.Content ref={scrollableRef}>
          <Modal.Body>
            <Box ref={targetRef} />
            <Survey onClose={() => close()} usersession={usersession} onStep={() => scrollIntoView()} />
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  )
}
